import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from '@react-router';
import { useAuth } from '@shared/components/AuthProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useSessionPersistedState } from '@shared/hooks/usePersistedState';
import { CultureCode, resolveEventLocale, updateLocaleLocalStorage, useGuestGeoLocale } from '@shared/hooks/useGeoLocale';
import { findAlpha2ByAlpha3, findCountryNameByAlpha2Code } from '@shared/hooks/useGeoLocale/countries';
import { buildAuthUrl, performFullPageRedirect } from '@shared/utils/navigation';
import { useEffectOnce } from '@shared/utils/hooks/useEffectOnce';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useMount } from '@shared/utils/hooks/useMount';
import { withWindow } from '@shared/utils/withWindow';
import { usePublicShopTelemetry } from '../../Shop.telemetry';
import { useIsIntlSupportedCatalogPath } from './useIsIntlSupportedCatalogPath';
import { CASH_FUNDS_PATH, SHOP_REGION_DIALOG_DISMISSED_KEY, SHOP_REGION_DIALOG_RESUME_PARAM, buildIntlAddGiftsPath } from './ShopRegionDialog.constants';
import type { GoToDestinationOptions, PendingEventChoice, ShopRegionDialogDestination, ShopRegionDialogGateArgs } from './ShopRegionDialog.types';

export const useShopRegionDialogGate = ({
  registryShippingAddress,
  isEventSelected,
  isIntlRestrictedEventSelected,
  intlEventHandles,
  isResolvingShopContext,
  eventPickerChoice,
  showEventPickerDialog,
}: ShopRegionDialogGateArgs) => {
  const { isLoggedIn } = useAuth();
  const hasMounted = useIsMounted();
  const { culture } = useGuestGeoLocale();
  const isOutsideUsRegion = isEventSelected ? isIntlRestrictedEventSelected : culture !== CultureCode.US;
  const { isResolved: isCatalogScopeResolved, isIntlSupported: isIntlSupportedCategory } = useIsIntlSupportedCatalogPath();
  const [isDismissed, setIsDismissed] = useSessionPersistedState<boolean>(SHOP_REGION_DIALOG_DISMISSED_KEY, false);
  // The picker reports every pick via a bumping nonce, so we watch that rather than inferring a
  // choice from selectedEventHandle: re-picking the already-selected event is a real choice, and
  // closing the picker without picking is not.
  const [pendingEventChoice, setPendingEventChoice] = useState<PendingEventChoice | null>(null);
  const { getValueString, buildRedirectPath } = useQueryParamHelper();
  const location = useLocation();
  const history = useHistory();

  const dismiss = useCallback(() => setIsDismissed(true), [setIsDismissed]);

  const goToIntlAddGifts = useCallback((eventHandle: string, shouldReplace: boolean = false) => performFullPageRedirect(buildIntlAddGiftsPath(eventHandle), shouldReplace), []);

  const resolveDestination = useCallback((): ShopRegionDialogDestination => {
    if (intlEventHandles.length === 1) return 'intlAddGifts';
    if (intlEventHandles.length > 1) return 'eventPicker';
    return isLoggedIn ? 'cashFunds' : 'login';
  }, [intlEventHandles, isLoggedIn]);

  const goToDestination = useCallback(
    (destination: ShopRegionDialogDestination, { shouldReplace = false }: GoToDestinationOptions = {}) => {
      switch (destination) {
        case 'intlAddGifts':
          return goToIntlAddGifts(intlEventHandles[0], shouldReplace);
        case 'eventPicker':
          setPendingEventChoice({ baselineNonce: eventPickerChoice?.nonce ?? 0, shouldReplace });
          return showEventPickerDialog();
        case 'login':
          return withWindow(global => {
            global.location.href = buildAuthUrl('/login', buildRedirectPath(`${location.pathname}${location.search}`, { [SHOP_REGION_DIALOG_RESUME_PARAM]: '1' }));
          });
        case 'cashFunds':
          return shouldReplace ? history.replace(CASH_FUNDS_PATH) : history.push(CASH_FUNDS_PATH);
      }
    },
    [intlEventHandles, goToIntlAddGifts, showEventPickerDialog, eventPickerChoice, buildRedirectPath, location.pathname, location.search, history],
  );

  const isResuming = getValueString(SHOP_REGION_DIALOG_RESUME_PARAM) === '1';
  const isSettled = hasMounted && !isResolvingShopContext;

  // Replace, don't push: the resume URL still carries intlGiftList=1, so a Back press onto it would
  // re-run this effect and throw the shopper forward again.
  useEffectOnce(isResuming && isSettled, () => {
    const destination = resolveDestination();
    goToDestination(destination === 'login' ? 'cashFunds' : destination, { shouldReplace: true });
  });

  // Consumed by the first outcome of the picker session we opened — a pick or a dismissal. Either
  // way the latch clears, so the shop's other picker entry points can't fire the hand-off later.
  useEffect(() => {
    if (!pendingEventChoice || !eventPickerChoice || eventPickerChoice.nonce <= pendingEventChoice.baselineNonce) return;

    const { eventHandle } = eventPickerChoice;
    setPendingEventChoice(null);
    if (eventHandle && intlEventHandles.includes(eventHandle)) {
      goToIntlAddGifts(eventHandle, pendingEventChoice.shouldReplace);
    }
  }, [pendingEventChoice, eventPickerChoice, intlEventHandles, goToIntlAddGifts]);

  useEffect(() => setPendingEventChoice(null), [location.pathname]);

  // Cash Funds and Charity ship worldwide, so the region hand-off has nothing to offer on those trees.
  const isPromptableCategory = isCatalogScopeResolved && !isIntlSupportedCategory;

  // isResuming is in the skip too: that load redirects immediately, so counting it as an exposure
  // would inflate the denominator with shoppers who never saw the dialog.
  const [isDialogEnabled] = useIsFeatureEnabled('registryShopRegionDialogEnabled', { skip: !isSettled || !isOutsideUsRegion || !isPromptableCategory || isResuming });

  return {
    isVisible: isSettled && isPromptableCategory && isDialogEnabled && !isDismissed && !isResuming && isOutsideUsRegion,
    registryCountryCode: registryShippingAddress?.countryCode ?? null,
    dismiss,
    resolveDestination,
    goToDestination,
  };
};

export type ShopRegionDialogContentArgs = Omit<ReturnType<typeof useShopRegionDialogGate>, 'isVisible'>;

export const useShopRegionDialogContent = ({ registryCountryCode, dismiss, resolveDestination, goToDestination }: ShopRegionDialogContentArgs) => {
  const { languageCodeIso15897, countryName: geoCountryName } = useGuestGeoLocale();
  const registryCountryName = registryCountryCode ? findCountryNameByAlpha2Code(findAlpha2ByAlpha3(registryCountryCode.toUpperCase()) ?? registryCountryCode) : undefined;
  const countryName = registryCountryName ?? geoCountryName;
  const [detectedLocale] = useState<string>(languageCodeIso15897);
  const [selectedLocale, setSelectedLocale] = useState<string>(languageCodeIso15897);
  const { shopRegionDialog, shopRegionDialogContinue, shopRegionDialogExploreCashFunds, shopRegionDialogLocaleChanged, shopRegionDialogDismissed } = usePublicShopTelemetry();

  useMount(() => {
    shopRegionDialog.enter(detectedLocale);
  });

  const onLocaleChange = useCallback(
    (nextLocale: string) => {
      const resolved = resolveEventLocale(nextLocale);
      if (!resolved) return;

      setSelectedLocale(nextLocale);
      updateLocaleLocalStorage(resolved);
      shopRegionDialogLocaleChanged({ detectedLocale, selectedLocale: nextLocale });

      if (resolved.culture === CultureCode.US) {
        dismiss();
      }
    },
    [detectedLocale, dismiss, shopRegionDialogLocaleChanged],
  );

  const onContinue = useCallback(() => {
    const destination = resolveDestination();
    shopRegionDialogContinue({ detectedLocale, selectedLocale, destination });
    dismiss();
    goToDestination(destination);
  }, [resolveDestination, goToDestination, dismiss, shopRegionDialogContinue, detectedLocale, selectedLocale]);

  const onExploreCashFunds = useCallback(() => {
    shopRegionDialogExploreCashFunds({ detectedLocale, selectedLocale });
    dismiss();
    goToDestination('cashFunds');
  }, [detectedLocale, selectedLocale, shopRegionDialogExploreCashFunds, dismiss, goToDestination]);

  const onClose = useCallback(() => {
    shopRegionDialogDismissed({ detectedLocale, selectedLocale });
    dismiss();
  }, [detectedLocale, selectedLocale, shopRegionDialogDismissed, dismiss]);

  return { countryName, selectedLocale, onLocaleChange, onContinue, onExploreCashFunds, onClose };
};
